import { useMemo } from 'react'
import { Outlet } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  TrainingFormsMembershipsDocument,
  TrainingKnowledgeFormMembershipsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { useBulkAction } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useBulkActionsConfig'
import { useColumns } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useColumns'
import { useDataSource } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useDataSource'
import { TrainingParticipantData } from 'modules/trainings/lib/TrainingParticipantData'
import {
  CompletionConfiguration,
  DetailOutletContext,
  TrainingsTraining,
  TrainingsTrainingClass,
} from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

const ParticipantsOneDataTable = ({
  training,
  trainingClasses,
  completionConfiguration,
  outletContext,
}: {
  training: TrainingsTraining
  trainingClasses: TrainingsTrainingClass[]
  completionConfiguration: CompletionConfiguration
  outletContext: DetailOutletContext
}) => {
  const { data: formData } = useQuery(TrainingFormsMembershipsDocument, {
    variables: { resourceId: String(training.id) },
    skip: !completionConfiguration?.knowledgeTestRequired,
    fetchPolicy: 'cache-and-network',
  })

  const knowledgeFormId = formData?.forms?.formsConnection?.edges?.[0]?.node?.id ?? ''

  const { data: participantsData } = useQuery(TrainingKnowledgeFormMembershipsDocument, {
    variables: {
      formId: knowledgeFormId,
    },
    skip: !completionConfiguration?.knowledgeTestRequired && !knowledgeFormId,
  })

  const trainingParticipantData = useMemo(
    () =>
      new TrainingParticipantData(
        training,
        trainingClasses ?? [],
        completionConfiguration,
        participantsData?.forms?.membershipsConnection?.nodes ?? null
      ),
    [training, trainingClasses, participantsData, completionConfiguration]
  )

  const source = useDataSource(training, trainingClasses)
  const columns = useColumns(trainingParticipantData)
  const handleBulkAction = useBulkAction(trainingClasses)

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns,
            },
          },
        ]}
      />
      <Outlet context={outletContext} />
    </>
  )
}

export default ParticipantsOneDataTable
